import { AnimatedImage } from "@/components/ui/animated-image";
import { Button } from "@/components/ui/button";
import { LoadingIcon } from "@/components/ui/loading-icon";
import { PublicReplyComposer } from "./PublicReplyComposer";
import type {
  CommunityPreview,
  PublicChannelContent,
  PublicContributor,
  PublicWorkspaceMetadata,
} from "@ano/shared/api/public-workspaces";

/** A single redacted conversation message — the denormalized sender name plus
 *  the kind-agnostic preview text the server exposes to non-members. */
export interface PreviewMessage {
  id: string;
  sender_name: string | null;
  text: string;
}

/**
 * Maps a fetched community preview into the landing's display state. Splits the
 * three outcomes the "Inside" section + the `public preview loaded` analytics
 * event both branch on:
 *   - `loaded`: at least one channel to show.
 *   - `empty`:  the community exists but has no public content yet.
 *
 * A fetch *failure* is handled by the caller (it can't be inferred from a
 * resolved value) and reported as `status: "error"` so a network blip never
 * masquerades as a genuinely empty community.
 */
export function resolvePreviewOutcome(preview: CommunityPreview | null): {
  status: "loaded" | "empty";
  channelCount: number;
} {
  if (!preview || preview.channels.length === 0) {
    return { status: "empty", channelCount: 0 };
  }
  return { status: "loaded", channelCount: preview.channels.length };
}

/** One conversation snippet — shared by the channel-focus hero and the
 *  "Inside" preview so the message-row markup lives in exactly one place. */
export function ConversationSnippet({
  messages,
}: {
  messages: PreviewMessage[];
}) {
  if (messages.length === 0) {
    return <p className="text-sm text-muted-foreground">Quiet so far.</p>;
  }
  return (
    <ul className="space-y-1.5">
      {messages.map((m) => (
        <li key={m.id} className="text-sm leading-relaxed">
          <span className="font-medium text-foreground/80">
            {m.sender_name ?? "Someone"}
          </span>{" "}
          <span className="text-muted-foreground">{m.text}</span>
        </li>
      ))}
    </ul>
  );
}

/** Sticky top bar with the always-reachable join CTA. */
export function AnonymousHeader({
  meta,
  onCta,
}: {
  meta: PublicWorkspaceMetadata;
  onCta: (action: "signup" | "signin", placement: "header") => void;
}) {
  return (
    <header className="flex shrink-0 items-center justify-between gap-3 border-b border-border px-4 py-3 sm:px-6">
      <div className="flex min-w-0 items-center gap-2.5">
        {meta.logo_url && (
          <div className="h-8 w-8 shrink-0 overflow-hidden rounded-lg bg-foreground/5">
            <AnimatedImage
              src={meta.logo_url}
              alt={meta.name}
              referrerPolicy="no-referrer"
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover"
            />
          </div>
        )}
        <span className="truncate text-sm font-semibold">{meta.name}</span>
        <span className="rounded-full bg-foreground/[0.06] px-2 py-0.5 text-[11px] text-muted-foreground">
          Public community
        </span>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onCta("signin", "header")}
        >
          Log in
        </Button>
        <Button size="sm" onClick={() => onCta("signup", "header")}>
          {meta.public_join_policy === "auto"
            ? "Sign up to join"
            : "Request access"}
        </Button>
      </div>
    </header>
  );
}

/** Name + tagline + stat line + featured-channel chips. */
export function CommunityHero({
  meta,
  stats,
}: {
  meta: PublicWorkspaceMetadata;
  stats: string[];
}) {
  const featured = meta.featured_channels ?? [];
  return (
    <div className="space-y-3">
      <h1 className="text-3xl font-semibold tracking-tight">{meta.name}</h1>
      {meta.tagline && (
        <p className="text-base text-foreground/90">{meta.tagline}</p>
      )}
      {stats.length > 0 && (
        <p className="text-sm text-muted-foreground">{stats.join(" · ")}</p>
      )}
      {featured.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-1">
          {featured.map((ch) => (
            <span
              key={ch.id}
              className="rounded-full bg-foreground/[0.06] px-2.5 py-1 text-xs"
            >
              <span className="text-muted-foreground">#</span>
              {ch.name}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/** Deep-link focus: when the URL is `/c/<channel>`, show that channel's
 *  content prominently so a Google click lands where the crawler saw it. The
 *  logged-out visitor can type a top-level reply right here; on Send it stashes
 *  the draft and one-click-signs-up via GitHub, then publishes after auth. */
export function ChannelFocusSection({
  channel,
  workspaceName,
  slug,
  autoJoin,
  onContinueWithGithub,
}: {
  channel: PublicChannelContent;
  workspaceName: string;
  slug: string;
  autoJoin: boolean;
  onContinueWithGithub: () => void;
}) {
  return (
    <section className="mt-8 space-y-3">
      <div className="flex items-baseline gap-2">
        <h2 className="text-lg font-semibold">
          <span className="text-muted-foreground">#</span>
          {channel.channel.name}
        </h2>
        {channel.channel.topic && (
          <p className="truncate text-sm text-muted-foreground">
            {channel.channel.topic}
          </p>
        )}
      </div>
      <div className="rounded-lg border border-border bg-foreground/[0.02] p-4">
        <ConversationSnippet messages={channel.messages} />
      </div>
      <p className="text-sm text-muted-foreground">
        One of the channels in {workspaceName} — sign up to join the
        conversation.
      </p>
      <PublicReplyComposer
        slug={slug}
        channelId={channel.channel.id}
        channelName={channel.channel.name}
        targetMessageId={null}
        autoJoin={autoJoin}
        placeholder={`Reply in #${channel.channel.name}…`}
        onContinueWithGithub={onContinueWithGithub}
      />
    </section>
  );
}

/** Live "peek inside" — featured channels with recent redacted messages, fading
 *  out at the bottom because there's always more behind the sign-up wall. */
export function InsidePreviewSection({
  workspaceName,
  status,
  preview,
}: {
  workspaceName: string;
  status: "loading" | "loaded" | "empty";
  preview: CommunityPreview | null;
}) {
  // Nothing to preview yet → render nothing: no bare "Inside X" header and no
  // copy advertising that the community is empty (which suppresses sign-ups).
  if (status === "empty") return null;
  return (
    <section className="mt-8">
      <h2 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Inside {workspaceName}
      </h2>
      {status === "loading" && (
        <div className="flex justify-center py-8">
          <LoadingIcon size={20} className="text-muted-foreground" />
        </div>
      )}
      {status === "loaded" && preview && (
        <div className="relative mt-3">
          <div className="space-y-5">
            {preview.channels.map((ch) => (
              <div key={ch.id} className="space-y-2">
                <div className="flex items-baseline gap-2">
                  <p className="text-sm font-semibold text-foreground">
                    <span className="text-muted-foreground">#</span>
                    {ch.name}
                  </p>
                  {ch.topic && (
                    <p className="truncate text-xs text-muted-foreground">
                      {ch.topic}
                    </p>
                  )}
                </div>
                <ConversationSnippet messages={ch.messages} />
              </div>
            ))}
          </div>
          {/* Fade out — there's always more behind the sign-up wall. */}
          <div className="pointer-events-none sticky bottom-0 -mt-16 h-16 bg-gradient-to-t from-background to-transparent" />
        </div>
      )}
    </section>
  );
}

/** Top contributors (recognition) — public display name + avatar + signal
 *  count only; never email. */
export function TopContributorsSection({
  contributors,
}: {
  contributors: PublicContributor[];
}) {
  return (
    <section className="mt-8 space-y-3">
      <h2 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Top contributors
      </h2>
      <div className="flex flex-wrap gap-2">
        {contributors.map((p) => (
          <div
            key={p.user_id}
            className="flex items-center gap-2 rounded-full border border-border bg-foreground/[0.03] py-1 pr-3 pl-1"
          >
            <div className="h-6 w-6 shrink-0 overflow-hidden rounded-full bg-foreground/10">
              {p.avatar_url && (
                <AnimatedImage
                  src={p.avatar_url}
                  alt={p.name ?? "Member"}
                  referrerPolicy="no-referrer"
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover"
                />
              )}
            </div>
            <span className="text-sm font-medium">{p.name ?? "Member"}</span>
            <span className="text-xs text-muted-foreground">
              {p.signal_count}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

/** Community guidelines (free-text rules) block. */
export function CommunityRulesSection({ rules }: { rules: string }) {
  return (
    <section className="mt-8 space-y-2">
      <h2 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Community guidelines
      </h2>
      <div className="whitespace-pre-wrap rounded-lg border border-border p-4 text-sm text-muted-foreground">
        {rules}
      </div>
    </section>
  );
}

/** Bottom conversion block — the primary sign-up push. */
export function ConversionBlock({
  workspaceName,
  publicJoinPolicy,
  onCta,
}: {
  workspaceName: string;
  publicJoinPolicy: "auto" | "request";
  onCta: (action: "signup" | "signin", placement: "conversion_block") => void;
}) {
  const isAutoJoin = publicJoinPolicy === "auto";
  return (
    <section className="mt-10 rounded-xl border border-border bg-foreground/[0.02] p-6 text-center">
      <h2 className="text-lg font-semibold">
        {isAutoJoin ? "Join the conversation" : "Request access"}
      </h2>
      <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
        {isAutoJoin
          ? `Create a free account to see every channel, reply, and get to know the people in ${workspaceName}.`
          : `Create a free account to request access to ${workspaceName}.`}
      </p>
      <div className="mt-4 flex justify-center gap-2">
        <Button onClick={() => onCta("signup", "conversion_block")}>
          {isAutoJoin ? `Sign up to join ${workspaceName}` : "Request access"}
        </Button>
        <Button
          variant="outline"
          onClick={() => onCta("signin", "conversion_block")}
        >
          I already have an account
        </Button>
      </div>
    </section>
  );
}
